import { useMemo } from "react";
import styled from "styled-components";
import { Flex } from "../../components/Flex";
import { H3 } from "../../components/Heading";
import { Loader } from "../../components/Loader";
import { Paragraph } from "../../components/Paragraph";
import { Slider } from "../../components/Slider";
import { CheckStatusEnum } from "../../context/MandateStatusContext";
import { useIndividualCheck } from "../../data/useIndividualCheck";
import {
  CheckContainer,
  CheckTable,
  CheckTableCell,
  CheckTableHeaderRow,
  CheckTitle,
  Section,
} from "./CheckStyledComponents";
import { CheckFields } from "./CheckFields";
import { CheckResults } from "./CheckResults";
import { IndividualChecksMap } from "./helper";
import { ChangesReviewManualAcceptReject } from "./ChangesReviewManualAcceptReject";
import { ApprovalComment } from "./approvalComment";
import { EntityDetailsField } from "../../types/Individual";

const CompactTableCell = styled(CheckTableCell)<{ changed?: boolean }>`
  ${({ changed, theme: { colors, space } }: any) => `
    padding: ${space[2]};
    border-bottom: 1px solid ${colors.gray[10]};
    background: transparent;
    color: ${changed ? colors.blue[100] : colors.gray[60]};
    vertical-align: top;
  `}
`;

const ResultPill = styled.span<{ changed: boolean }>`
  ${({ changed, theme: { colors, space } }: any) => `
    display: inline-block;
    border-radius: 999px;
    padding: ${space[0]} ${space[1]};
    background: ${changed ? "#FFF3D6" : colors.gray[10]};
    color: ${changed ? "#8A5A00" : colors.gray[50]};
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  `}
`;

const normalizeValue = (value: any) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "object") {
    const next = value.name ?? value.status ?? value.label ?? value.id ?? "";
    return typeof next === "object" ? normalizeValue(next) : String(next);
  }
  return String(value).trim();
};

const titleCaseValue = (value: string) =>
  value.replace(/\b\w/g, (character) => character.toUpperCase());

const isStatusField = (field?: string) =>
  field?.toLowerCase().includes("status") ?? false;

const displayValue = (value: any, shouldTitleCase = false) => {
  const normalized = normalizeValue(value);
  if (!normalized) return "-";

  return shouldTitleCase ? titleCaseValue(normalized) : normalized;
};

const isChangedStatus = (status?: string) =>
  status?.replace(/\s+/g, "").toLowerCase() === "changed";

export const EntityDetailsCheck = (props: any) => {
  const { isOpen, onRequestClose, id, useSlider = true } = props;

  const { data: individualCheckData, isLoading: isLoadingIndividualCheck } =
    useIndividualCheck(id);

  const check = individualCheckData?.data;
  const instance: any = check?.instance;

  const fields: EntityDetailsField[] = useMemo(() => {
    return Array.isArray(instance?.fields) ? instance.fields : [];
  }, [instance?.fields]);

  const changedFields = fields.filter((field) => isChangedStatus(field.status));
  const reviewChanges = changedFields.map((field) => ({
    field: field.field,
    oldValue: displayValue(field.baseline, isStatusField(field.field)),
    newValue: displayValue(field.submitted, isStatusField(field.field)),
  }));

  const content = (
    <CheckContainer>
      <Flex justifyContent="space-between" alignItems="flex-start">
        <CheckTitle>
          {IndividualChecksMap.entity_details_match_check.title}
        </CheckTitle>

        {check?.status?.status === CheckStatusEnum.Review && (
          <ChangesReviewManualAcceptReject
            id={id}
            title={IndividualChecksMap.entity_details_match_check.title}
            changes={reviewChanges}
            onSuccess={() => onRequestClose(false)}
          />
        )}
      </Flex>

      {check?.status?.status && <CheckResults status={check.status.status} />}

      {instance?.approval_comment && (
        <ApprovalComment
          data={instance.approval_comment}
          status={check?.status?.status}
        />
      )}

      <CheckFields
        individual_checkfield_result={check?.individual_checkfield_result || []}
      />

      <Section>
        <Flex justifyContent="space-between" alignItems="center" mb={2}>
          <H3 mb={0}>Details</H3>
          <Paragraph color="gray.60" fontSize={1} my={0}>
            {changedFields.length} change{changedFields.length === 1 ? "" : "s"}{" "}
            detected
          </Paragraph>
        </Flex>

        <CheckTable>
          <thead>
            <CheckTableHeaderRow>
              <CheckTableCell>Field</CheckTableCell>
              <CheckTableCell>Profile</CheckTableCell>
              <CheckTableCell>Submitted</CheckTableCell>
              <CheckTableCell>Result</CheckTableCell>
            </CheckTableHeaderRow>
          </thead>
          <tbody>
            {fields.map((field, index) => {
              const changed = isChangedStatus(field.status);

              return (
                <tr key={`${field.field}-${index}`}>
                  <CompactTableCell changed={changed}>
                    <Paragraph fontWeight={600} my={0}>
                      {field.field}
                    </Paragraph>
                  </CompactTableCell>
                  <CompactTableCell changed={changed}>
                    {displayValue(field.baseline, isStatusField(field.field))}
                  </CompactTableCell>
                  <CompactTableCell changed={changed}>
                    {displayValue(field.submitted, isStatusField(field.field))}
                  </CompactTableCell>
                  <CompactTableCell changed={changed}>
                    <ResultPill changed={changed}>
                      {changed ? "CHANGED" : "NO CHANGE"}
                    </ResultPill>
                  </CompactTableCell>
                </tr>
              );
            })}
          </tbody>
        </CheckTable>
      </Section>
    </CheckContainer>
  );

  return (
    <>
      {isLoadingIndividualCheck ? (
        <Flex justifyContent="center" alignItems="center" minHeight="60vh">
          <Loader />
        </Flex>
      ) : useSlider ? (
        <Slider
          isOpen={isOpen}
          onRequestClose={() => onRequestClose(false)}
          width="70%"
        >
          {content}
        </Slider>
      ) : (
        content
      )}
    </>
  );
};
